import { i18next } from '@yola/ws-sdk';
import elementSettings from 'src/js/modules/element-settings';
import getUniqueValues from 'src/js/modules/utils/get-unique-values';
import controlIds from '../constants/control-ids';
import settingIds from '../constants/setting-ids';
import attributes from '../constants/attributes';
import previewGifs from '../constants/preview-gifs';
import computeCornersFallback from '../helpers/compute-corners-fallback';
import computeSpacingFallback from '../helpers/compute-spacing-fallback';
import getColorScheme from './color-scheme';
import getCorners from './corners';
import getHorizontalContentAlignment from './horizontal-content-alignment';
import getHorizontalGap from './horizontal-gap';
import getHorizontalSpacing from './horizontal-spacing';
import getVerticalContentAlignment from './vertical-content-alignment';
import getVerticalGap from './vertical-gap';
import getVerticalSpacing from './vertical-spacing';

const {
  helpers: { getTargetElementIds },
} = elementSettings;

const getColumnsControlSettings = (elementId) => {
  const settingId = settingIds.ALL_COLUMNS;
  const elementIds = getTargetElementIds(elementId);
  const columnElementIds = getTargetElementIds(elementId, 'ws-column');

  const controlSettings = {
    [controlIds.COLOR_SCHEME]: getColorScheme(settingId, { elementIds: columnElementIds }),
    [controlIds.HORIZONTAL_CONTENT_ALIGNMENT]: getHorizontalContentAlignment(settingId, {
      elementIds: columnElementIds,
    }),
    [controlIds.VERTICAL_CONTENT_ALIGNMENT]: getVerticalContentAlignment(settingId, {
      elementIds: columnElementIds,
    }),
    [controlIds.NO_WRAP]: {
      id: controlIds.NO_WRAP,
      glyph: 'corner-down-left',
      label: i18next.t("Columns that don't fit wrap to the next row."),
      attributes: [attributes.NO_WRAP],
      elementIds,
    },
    [controlIds.COLUMN_WIDTH]: {
      settingId,
      id: controlIds.COLUMN_WIDTH,
      title: i18next.t('Column width'),
      attributes: [attributes.WIDTH],
      elementIds,
      choices: [
        { name: i18next.t('Fixed'), value: 'fixed' },
        { name: i18next.t('Fill'), value: 'fill' },
      ],
      popover: {
        content: i18next.t('Stretch columns to the full width of the container'),
        imgSrc: previewGifs.COLUMNS_WIDTH_GIF,
      },
    },
    [controlIds.COLUMN_HEIGHT]: {
      settingId,
      id: controlIds.COLUMN_HEIGHT,
      title: i18next.t('Column height'),
      attributes: [attributes.HEIGHT],
      elementIds,
      choices: [
        { name: i18next.t('Hug'), value: 'hug' },
        { name: i18next.t('Fill'), value: 'fill' },
      ],
      popover: {
        content: i18next.t('Stretch columns to match the tallest one'),
        imgSrc: previewGifs.COLUMNS_HEIGHT_GIF,
      },
    },
    [controlIds.BORDER]: {
      settingId,
      id: controlIds.BORDER,
      title: i18next.t('Border'),
      attributes: [attributes.BORDER],
      elementIds: columnElementIds,
    },
    [controlIds.SHADOW]: {
      settingId,
      id: controlIds.SHADOW,
      title: i18next.t('Shadow'),
      attributes: [attributes.SHADOW],
      elementIds: columnElementIds,
    },
    [controlIds.CORNERS]: getCorners(settingId, { elementIds: columnElementIds }),
    [controlIds.VERTICAL_SPACING]: getVerticalSpacing(settingId, {
      elementIds: columnElementIds,
    }),
    [controlIds.HORIZONTAL_SPACING]: getHorizontalSpacing(settingId, {
      elementIds: columnElementIds,
    }),
    [controlIds.HORIZONTAL_GAP]: getHorizontalGap(settingId, { elementIds }),
    [controlIds.VERTICAL_GAP]: getVerticalGap(settingId, { elementIds }),
  };

  return {
    ...controlSettings,
    [controlIds.CORNERS_SECTION]: {
      settingId,
      id: controlIds.CORNERS_SECTION,
      title: i18next.t('Corners'),
      elementIds: getUniqueValues(controlSettings[controlIds.CORNERS].elementIds),
      attributes: getUniqueValues(controlSettings[controlIds.CORNERS].attributes),
      computeFallbackValue: computeCornersFallback,
    },
    [controlIds.SPACING_SECTION]: {
      settingId,
      id: controlIds.SPACING_SECTION,
      title: i18next.t('Spacing'),
      elementIds: getUniqueValues([
        ...controlSettings[controlIds.VERTICAL_SPACING].elementIds,
        ...controlSettings[controlIds.HORIZONTAL_SPACING].elementIds,
        ...controlSettings[controlIds.HORIZONTAL_GAP].elementIds,
        ...controlSettings[controlIds.VERTICAL_GAP].elementIds,
      ]),
      attributes: getUniqueValues([
        ...controlSettings[controlIds.VERTICAL_SPACING].attributes,
        ...controlSettings[controlIds.HORIZONTAL_SPACING].attributes,
        ...controlSettings[controlIds.HORIZONTAL_GAP].attributes,
        ...controlSettings[controlIds.VERTICAL_GAP].attributes,
      ]),
      computeFallbackValue: computeSpacingFallback,
    },
  };
};

export default getColumnsControlSettings;
